import React, { createContext, ReactNode, useContext, useEffect, useMemo } from "react";
import { AiEventStreamStore } from "../store/AiEventStreamStore";
import { API_HOST } from "../config/_entrypoint";

type AiEventStreamProviderProps = {
  children: ReactNode;
  eventStreamPath?: string;
  eventName?: string;
  withCredentials?: boolean;
  maxStoredEvents?: number;
  maxStoredDeltas?: number;
  streamUrlFactory?: (streamId: string) => string;
};

const defaultEventStreamPath = "api/ai/event_stream";

const AiEventStreamContext = createContext<AiEventStreamStore | null>(null);

const buildEventStreamUrl = (apiHost: string, eventStreamPath: string, streamId: string): string => {
  const normalizedApiHost = apiHost.endsWith("/") ? apiHost : `${apiHost}/`;
  const normalizedPath = eventStreamPath.replace(/^\/+|\/+$/g, "");

  return `${normalizedApiHost}${normalizedPath}/${encodeURIComponent(streamId)}`;
};

export function AiEventStreamProvider({
  children,
  eventStreamPath = defaultEventStreamPath,
  eventName = "aiEvent",
  withCredentials = false,
  maxStoredEvents,
  maxStoredDeltas,
  streamUrlFactory,
}: AiEventStreamProviderProps) {
  const apiHost = API_HOST;

  const resolvedStreamUrlFactory = useMemo(() => {
    if (streamUrlFactory) {
      return streamUrlFactory;
    }

    if (!apiHost) {
      throw new Error("AiEventStreamProvider requires either apiHost or streamUrlFactory.");
    }

    return (streamId: string) => buildEventStreamUrl(apiHost, eventStreamPath, streamId);
  }, [apiHost, eventStreamPath, streamUrlFactory]);

  const store = useMemo(
    () =>
      new AiEventStreamStore({
        streamUrlFactory: resolvedStreamUrlFactory,
        eventName,
        withCredentials,
        maxStoredEvents,
        maxStoredDeltas,
      }),
    [eventName, maxStoredDeltas, maxStoredEvents, resolvedStreamUrlFactory, withCredentials]
  );

  useEffect(() => {
    return () => {
      store.close();
    };
  }, [store]);

  return <AiEventStreamContext.Provider value={store}>{children}</AiEventStreamContext.Provider>;
}

export function useAiEventStreamStore(): AiEventStreamStore {
  const store = useContext(AiEventStreamContext);

  if (!store) {
    throw new Error("useAiEventStreamStore must be used inside AiEventStreamProvider.");
  }

  return store;
}
